'use client';

import React, { useEffect, useState } from 'react';
import BannerHeader from './BannerHeader';
import ProgressBar from './ProgressBar';
import TextArea from './TextArea';
import BackButton from './BackButton';
import CardItem from './CardItem';
import './Form.css';

import { InputField } from '@kohler-dsm-library/kohler-core';
import bannerFormApiService from '../../../aem/services/bannerFormApiService';
import { getValidationMap, validateFormFields } from '@/components/utils/formValidation';
import {
  isAnalyticsEnabled,
  trackFormError,
  trackFormInteraction,
  trackFormProgress,
  trackFormSubmission,
} from '@/analytics';
import { stripHtml } from '@/aem-dsm-mappers/utils/utils';
import {
  readStoredCampaignPhone,
  readStoredDefaultCampaignPhone,
} from '@/utils/campaignPhoneStorage';

const getName = (name) => name?.toLowerCase().replaceAll(/\s+/g, '');
const toDigitsOnly = (value = '') => String(value).replace(/\D/g, '');

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const Form = (props) => {
  const {
    formVariant,
    bannerLink,
    bannerMessage,
    thankyouBannerMessage,
    businessUnitOverride,
    analytics,
    customFields = [],
    formFields = [],
    disclaimer,
    steps = [],
    isProductMultiSelect = false,
  } = props || {};

  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState({});
  const [backendError, setBackendError] = useState(false);
  const [businessGroupSelections, setBusinessGroupSelections] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationMap, setValidationMap] = useState({});
  const [validationMapLoaded, setValidationMapLoaded] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const currentStep = steps[stepIndex] || null;
  const cards = currentStep?.cards ?? [];
  const supportsMultiSelect = formVariant === 'paginated' && Boolean(isProductMultiSelect);
  const analyticsEnabled = isAnalyticsEnabled(analytics);
  const allFields = formVariant === 'paginated' ? steps.flatMap((s) => s.fields) : formFields;
  const generateCurrentFormState = (fields) => {
    return fields.reduce((acc, field) => {
      if (field.type !== 'button') {
        acc[getName(field.name)] = field.type === 'checkbox' ? false : '';
      }
      return acc;
    }, {});
  };

  const allControlFields = allFields?.filter((f) => f.type !== 'button') || [];

  const initialState = {
    ...(formVariant === 'paginated' ? { businessgroup: '' } : {}),
    ...generateCurrentFormState(allFields),
  };

  const [formData, setFormData] = useState(initialState);

  const formatPhoneNumber = (value) => {
    const digits = value.replaceAll(/\D/g, '').slice(0, 10);

    if (!digits) return '';
    if (digits.length < 4) return digits;

    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    let updatedValue = type === 'checkbox' ? checked : value;

    if (name === 'phonenumber') {
      updatedValue = formatPhoneNumber(value);
    }
    const updatedFormData = {
      ...formData,
      [name]: updatedValue,
    };

    setFormData(updatedFormData);

    validateSingleField(name, updatedFormData, validationMap);
  };

  const updateFieldError = (fieldKey, fieldError) => {
    setError((prev) => {
      const nextErrors = { ...prev };

      if (fieldError) {
        nextErrors[fieldKey] = fieldError;
      } else {
        delete nextErrors[fieldKey];
      }

      return nextErrors;
    });
  };

  const validateFields = (
    fieldsToValidate,
    values = formData,
    activeValidationMap = validationMap,
  ) => validateFormFields(fieldsToValidate, values, activeValidationMap, allControlFields);

  const validateSingleField = (
    fieldKey,
    values = formData,
    activeValidationMap = validationMap,
  ) => {
    const fieldConfig = allControlFields.find((field) => getName(field.name) === fieldKey);

    if (!fieldConfig) return;

    const nextErrors = validateFields([fieldConfig], values, activeValidationMap);
    updateFieldError(fieldKey, nextErrors[fieldKey]);
  };

  const handleSelectChange = (e) => {
    const nextValue = stripHtml(e.target.value);
    const nextFormData = {
      ...formData,
      [e.target.name]: nextValue,
    };

    setFormData((prev) => ({
      ...prev,
      [e.target.name]: nextValue,
    }));
    validateSingleField(e.target.name, nextFormData);
  };

  const getTitles = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return '';
    }

    return arr
      .map((item) => item?.title)
      .filter((title) => typeof title === 'string' && title.trim() !== '')
      .join(', ');
  };

  const storedPhone = readStoredCampaignPhone() || null;
  const storedDefaultPhone = readStoredDefaultCampaignPhone() || null;

  const urlObj = typeof window === 'undefined' ? null : new URL(window.location.href);

  const cleanUrl = urlObj ? urlObj.origin + urlObj.pathname : '';

  const utmSource = urlObj ? urlObj.searchParams.get('utm_source') || '' : '';

  const utm_campaign = urlObj ? urlObj.searchParams.get('utm_campaign') || '' : '';

  const utm_medium = urlObj ? urlObj.searchParams.get('utm_medium') || '' : '';

  const utm_term = urlObj ? urlObj.searchParams.get('utm_term') || '' : '';

  const utm_content = urlObj ? urlObj.searchParams.get('utm_content') || '' : '';

  const subId = urlObj ? urlObj.searchParams.get('subId') || '' : '';
  const campaignSourceParam = urlObj
    ? urlObj.searchParams.get('src') ||
      urlObj.searchParams.get('source') ||
      urlObj.searchParams.get('utm_source') ||
      ''
    : '';

  const campaignContext = {
    src: campaignSourceParam,
    source: campaignSourceParam,
    utm_source: utmSource,
    utm_campaign,
    utm_medium,
    utm_term,
    utm_content,
    sub_id: subId,
  };

  const normalizeBusinessUnit = (value) =>
    typeof value === 'string' ? stripHtml(value).trim() : '';

  const resolvedCampaignPhone =
    typeof storedPhone === 'string' ? storedPhone.replace(/\D/g, '') : '';
  const defaultCampaignPhone = typeof storedDefaultPhone === 'string' ? storedDefaultPhone : '';
  const isOrganicCampaign =
    resolvedCampaignPhone &&
    defaultCampaignPhone &&
    toDigitsOnly(resolvedCampaignPhone) === toDigitsOnly(defaultCampaignPhone);

  const resolvedBusinessUnit =
    normalizeBusinessUnit(formData.businessgroup) ||
    normalizeBusinessUnit(formData.businessunit) ||
    normalizeBusinessUnit(businessUnitOverride) ||
    normalizeBusinessUnit(props.businessGroup) ||
    'Shower';

  const payloadFields = {
    marketingconsent_koh: 'True',
    serviceconsent_koh: 'True',
    trustedform_cert_url: '',
    campaignnumber_koh: resolvedCampaignPhone,
    channel_koh: resolvedCampaignPhone ? (isOrganicCampaign ? 'Organic' : 'Paid') : '',
    websource_koh: cleanUrl,
    businessgroup_koh: resolvedBusinessUnit,
    utm_campaign_koh: utm_campaign || 'Organic',
    utm_source_koh: utmSource || 'Organic',
    utm_medium_koh: utm_medium,
    utm_term_koh: utm_term,
    utm_content_koh: utm_content,
    sub_id_koh: subId,
    product: resolvedBusinessUnit,
    website: urlObj,
    campaign_number: resolvedCampaignPhone,
    ...(businessGroupSelections ? { meta_koh: getTitles(businessGroupSelections) } : {}),
  };

  const safeTrackAnalytics = (trackEvent) => {
    if (!analyticsEnabled) return;

    try {
      trackEvent();
    } catch (error) {
      console.warn('Form analytics tracking failed.', error);
    }
  };

  const trackSubmissionEvent = () => {
    safeTrackAnalytics(() =>
      trackFormSubmission({
        analytics,
        campaign: campaignContext,
        defaultCampaignNumber: defaultCampaignPhone,
        storedPhone,
        formVariant,
        formData,
      }),
    );
  };

  const trackErrorEvent = (errorMessage) => {
    if (!errorMessage) return;

    safeTrackAnalytics(() =>
      trackFormError({
        analytics,
        campaign: campaignContext,
        defaultCampaignNumber: defaultCampaignPhone,
        storedPhone,
        formVariant,
        formData,
        errorMessage,
      }),
    );
  };

  const trackProgressEvent = (step) => {
    safeTrackAnalytics(() =>
      trackFormProgress({
        analytics,
        campaign: campaignContext,
        defaultCampaignNumber: defaultCampaignPhone,
        storedPhone,
        formVariant,
        stepIndex: stepIndex + 1,
        stepName: step?.stepName,
      }),
    );
  };

  const trackRadioInteractionEvent = (optionValue) => {
    if (!optionValue) return;

    safeTrackAnalytics(() =>
      trackFormInteraction({
        fieldType: 'radio',
        fieldLabel: optionValue,
        eventValue: 1,
      }),
    );
  };

  useEffect(() => {
    const getValidation = async () => {
      const data = await getValidationMap();
      setValidationMap(data || {});
      setValidationMapLoaded(true);
    };

    if (!validationMapLoaded) {
      getValidation();
    }
  }, [validationMapLoaded]);

  const ensureValidationMap = async () => {
    if (validationMapLoaded) {
      return validationMap;
    }

    const data = (await getValidationMap()) || {};
    setValidationMap(data);
    setValidationMapLoaded(true);
    return data;
  };

  const submitForm = async (payload, { onSuccess, onError } = {}) => {
    try {
      const response = await bannerFormApiService.submitLead(payload, 'submit');
      console.log('LeadConduit Response:', response);
      if (response.outcome === 'success') {
        setBackendError(false);
        onSuccess?.(); // run success callback
      } else {
        setBackendError(true);
        onError?.('There was an issue submitting the request. Please try again.');
      }
    } catch (err) {
      console.error('LeadConduit Error:', err);
      setBackendError(true);
      onError?.('There was an issue submitting the request. Please try again.');
    } finally {
      setIsSubmitted(false);
    }
  };

  const getRecaptchaToken = () => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha?.enterprise) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
          .then(resolve)
          .catch(reject);
      });
    });
  };

  const handleStepSubmit = async () => {
    if (Object.keys(error).length > 0) {
      trackErrorEvent(Object.values(error)[0]);
      return;
    }

    if (stepIndex === steps.length - 1) {
      setIsSubmitted(true);
      // STEP 2: Generate reCAPTCHA v3 token on form submission
      if (!window.grecaptcha?.enterprise) {
        trackErrorEvent('reCAPTCHA not loaded');
        return;
      }

      let recaptchaToken;

      try {
        recaptchaToken = await getRecaptchaToken();
      } catch (error) {
        // Enabling the submit button if reCAPTCHA token generation failed
        setIsSubmitted(false);
        trackErrorEvent('reCAPTCHA token generation failed');
      }

      const res = await fetch('/api/recaptcha-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: recaptchaToken, //  CORRECT KEY
          action: 'submit', // MATCH expectedAction
        }),
      });

      if (!res.ok) {
        // Enabling the submit button if submit failed due to bot detection
        setIsSubmitted(false);
        console.log('Submission failed due to bot detection.');
        setError('Submission failed due to bot detection.');
        return;
      }

      const payload = {
        ...formattedPayload,
        ...payloadFields,
        token: recaptchaToken, // rename key
        action: 'submit', // optional but recommended
      };
      trackSubmissionEvent();
      submitForm(payload, {
        onSuccess: () => {
          setIsSuccess(true);
        },
        onError: trackErrorEvent,
      });
    } else {
      trackProgressEvent(currentStep);
      setStepIndex(stepIndex + 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(error).length > 0) {
      trackErrorEvent(Object.values(error)[0]);
      return;
    }

    // STEP 2: Generate reCAPTCHA v3 token on form submission
    if (!window.grecaptcha?.enterprise) {
      trackErrorEvent('reCAPTCHA not loaded');
      return;
    }

    let recaptchaToken;
    setIsSubmitted(true);

    try {
      recaptchaToken = await getRecaptchaToken();
    } catch (error) {
      // Enabling the submit button if reCAPTCHA token generation failed
      setIsSubmitted(false);
      trackErrorEvent('reCAPTCHA token generation failed');
    }

    const res = await fetch('/api/recaptcha-verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: recaptchaToken, //  CORRECT KEY
        action: 'submit', // MATCH expectedAction
      }),
    });

    if (!res.ok) {
      // Enabling the submit button if submit failed due to bot detection
      setIsSubmitted(false);
      console.log('Submission failed due to bot detection.');
      setError('Submission failed due to bot detection.');
      return;
    }

    // Build payload and include token
    const payload = {
      ...formattedPayload,
      ...payloadFields,
      token: recaptchaToken, // rename key
      action: 'submit', // optional but recommended
    };

    trackSubmissionEvent();

    submitForm(payload, {
      onSuccess: () => {
        setIsSuccess(true);
      },
      onError: trackErrorEvent,
    });
  };

  const handleBlur = async (event) => {
    const activeValidationMap = await ensureValidationMap();
    validateSingleField(event.target.name, formData, activeValidationMap);
  };

  const handleReset = () => {
    setFormData(initialState);
    setBusinessGroupSelections(null);
    setIsSuccess(false);
    setIsSubmitted(false);
    setBackendError(false);
    if (formVariant === 'paginated') {
      setStepIndex(0);
    }
  };

  const payloadKeyMap = {
    firstname: 'first_name',
    lastname: 'last_name',
    phonenumber: 'phone_1',
    zipcode: 'postal_code',
    email: 'email',
    personalizedmessage: 'Comments',
    companyname: 'campaign_id',
    companyaddress: 'address_1',
    companystate: 'state',
    companywebsite: 'website',
    numberofemployees: 'company.number_employees',
    areasserved: 'company.industry',
    fullname: 'first_name',
    comments: 'Comments',
  };

  const transformPayload = (data) => {
    const newPayload = {};

    Object.keys(data).forEach((key) => {
      const newKey = payloadKeyMap[key] || key; // fallback if not mapped
      newPayload[newKey] = data[key];
    });

    return newPayload;
  };

  const formattedPayload = transformPayload(formData);

  const isFormInvalid = Object.values(formattedPayload).some(
    (value) => !value || value.toString().trim() === '',
  );

  const isCurrentStepInvalid = () => {
    if (currentStep?.cards && currentStep?.cards?.length > 0) {
      return !formData?.businessgroup;
    }

    const stepFields = currentStep?.fields || [];

    return stepFields?.some((field) => {
      // ignore button
      if (field?.type === 'button') return false;

      const controlField = allControlFields?.find((ctrl) => ctrl?.id === field?.id);
      if (!controlField) return false;
      const name = getName(controlField?.name);
      const value = formData[name];
      return !value || value?.trim() === '';
    });
  };

  const handleBack = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const handleCardClick = (index) => {
    if (index === null || index === undefined) return;

    const prev = Array.isArray(selectedIndexes) ? selectedIndexes : [];

    let updatedIndexes = [];

    if (supportsMultiSelect) {
      // MULTI SELECT (toggle)
      updatedIndexes = prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
    } else {
      // SINGLE SELECT
      updatedIndexes = [index];
    }

    // update UI state FIRST
    setSelectedIndexes(updatedIndexes);

    const safeCards = Array.isArray(cards) ? cards : [];

    const selectedCards = updatedIndexes.map((i) => safeCards?.[i] ?? null).filter(Boolean);

    const selectedValues = selectedCards.map((card) => card?.value ?? null).filter(Boolean);

    const selectedOptions = selectedCards.map((card, idx) => ({
      index: updatedIndexes[idx],
      title: card?.cardTitle ?? '',
      value: card?.value ?? null,
    }));

    const uniqueValues = [...new Set(selectedValues)];
    const finalValue = uniqueValues.length ? uniqueValues[0] : null;

    // update formData
    setFormData?.((prev) => ({
      ...(prev || {}),
      businessgroup: finalValue,
    }));
    setBusinessGroupSelections(selectedOptions);
  };

  const getFormControlFields = (fields = []) => {
    return fields?.filter((f) => f.type !== 'button');
  };

  const renderField = (field, index) => {
    const name = getName(field.name);
    switch (field.type) {
      case 'text':
        return (
          <div key={index} className={`col-${field.col}`}>
            <InputField
              key={index}
              {...field}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!error[name]}
              description={error[name]}
            />
          </div>
        );
      case 'textarea':
        return (
          <TextArea
            key={index}
            label={field.label}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error[name]}
            description={error[name]}
          />
        );
      case 'select':
        return (
          <div key={index} className={`col-${field.col} select-wrapper`}>
            <select key={index} name={name} value={formData[name]} onChange={handleSelectChange}>
              <option value="">Select {field.label}</option>
              {field.options.map((opt, i) => (
                <option key={i} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {error[name] ? <p className="field-error-msg">{error[name]}</p> : null}
          </div>
        );
      case 'radio':
        return (
          <div key={index} className={`col-${field.col} radio-group`}>
            <p>{field.label}</p>
            {field.options.map((opt, i) => (
              <label key={i}>
                <input
                  type="radio"
                  name={name}
                  value={opt}
                  checked={formData[name] === opt}
                  onChange={(event) => {
                    handleChange(event);
                    validateSingleField(name, {
                      ...formData,
                      [name]: event.target.value,
                    });
                    trackRadioInteractionEvent(opt);
                  }}
                />
                {opt}
              </label>
            ))}
            {error[name] ? <p className="field-error-msg">{error[name]}</p> : null}
          </div>
        );
      case 'checkbox':
        return (
          <label key={index} className={`col-${field.col} checkbox`}>
            <input
              type="checkbox"
              name={name}
              checked={Boolean(formData[name])}
              onChange={(event) => {
                handleChange(event);
                validateSingleField(name, {
                  ...formData,
                  [name]: event.target.checked,
                });
              }}
            />
            {field.label}
            {error[name] ? <p className="field-error-msg">{error[name]}</p> : null}
          </label>
        );
      case 'button':
        return (
          <div key={index} className="col-12">
            <button
              type="button"
              className="cta-btn"
              disabled={
                formVariant.includes('paginated')
                  ? isSubmitted || isCurrentStepInvalid()
                  : isSubmitted || isFormInvalid
              }
              onClick={() =>
                formVariant.includes('paginated') ? handleStepSubmit() : handleSubmit()
              }
            >
              {field.label}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  if (isSuccess && thankyouBannerMessage) {
    return (
      <div className="form-container thank-you-bann">
        <BannerHeader
          title={thankyouBannerMessage?.title}
          subtitle={thankyouBannerMessage?.subtitle}
        />
        <p className="thankyou-message">{thankyouBannerMessage?.message?.value}</p>
        <button className="cta-btn" onClick={() => handleReset()}>
          {thankyouBannerMessage?.buttonText ? thankyouBannerMessage?.buttonText : 'Done'}
        </button>
      </div>
    );
  }

  if (formVariant === 'paginated') {
    return (
      <div className="white-mod form-container">
        <BannerHeader
          title={currentStep?.bannerMessage?.title}
          subtitle={currentStep?.bannerMessage?.subtitle}
        />
        <ProgressBar current={stepIndex + 1} total={steps.length} />

        {currentStep?.customFields?.length > 0 && (
          <div className="custom-fields">
            {currentStep.customFields.map((c, i) => (
              <React.Fragment key={i}>
                {c.key && c.key.toLowerCase() !== 'copy' && <h3>{c.key}</h3>}
                {c.value && <p>{c.value}</p>}
              </React.Fragment>
            ))}
          </div>
        )}
        <div className="custom-fields">
          <React.Fragment>
            {currentStep?.bannerMessage?.message && (
              <h3>{currentStep?.bannerMessage?.message?.value}</h3>
            )}
          </React.Fragment>
        </div>
        <div className="grid">
          {getFormControlFields(currentStep.fields)?.map(renderField)}
          {Array.isArray(cards) && cards.length > 0 && (
            <div className="card-grid">
              {cards.map((card, i) => (
                <CardItem
                  key={i}
                  card={card}
                  index={i}
                  selectedIndexes={selectedIndexes}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          )}
          <p className="disclaimer">{currentStep.disclaimer}</p>
          {currentStep.fields?.filter((f) => f.type === 'button').map(renderField)}
        </div>
        {stepIndex > 0 && <BackButton onClick={handleBack} />}
        {backendError ? (
          <p className="form-error-msg">
            There was an issue submitting the request. Please try again.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="white-mod form-container">
      {bannerLink && bannerMessage && (
        <BannerHeader title={bannerMessage?.title} subtitle={bannerMessage?.subtitle} />
      )}

      {customFields.length > 0 && (
        <div className="custom-fields">
          {customFields.map((c, i) => {
            return (
              <React.Fragment key={i}>
                {c.key && c.key.toLowerCase() !== 'copy' && <h3>{c.key}</h3>}
                {c.value && <p>{c.value}</p>}
              </React.Fragment>
            );
          })}
        </div>
      )}
      <div className="grid">
        {getFormControlFields(formFields)?.map(renderField)}
        <p className="disclaimer">{disclaimer}</p>
        {formFields?.filter((f) => f.type === 'button').map(renderField)}
      </div>
      {backendError ? (
        <p className="form-error-msg">
          There was an issue submitting the request. Please try again.
        </p>
      ) : null}
    </div>
  );
};

export default Form;
